import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCCSchedulerHighlightStore } from '@lp/core/store/scheduler';
import { getResourceShiftTotalsMap, withoutTzOffset } from '@lp/features/Scheduler/utils';
import {
  createSchedulerDataHook,
  createSchedulerMutationActionsHook,
  createWorkdaySyncHook,
  createUseShiftChangeHook,
  createUseUnblockShiftHook,
  createSchedulerShiftHighlightHook,
} from '@lp/features/Scheduler/factories';
import { transformCountryViolations, transformResourcesViolations } from '@lp/features/Scheduler/validation.utils';
import {
  CCResourcesWithShifts,
  DateRangeParams,
  SetCCShiftData,
  MonthSchedule,
  PlanningProcess,
  PlanningProcessStatus,
  ShiftStatus,
} from '@/api/apis/resources/schedule';
import {
  useCCCancelGenerationPlanningMutation,
  useCCCancelPlanningMutation,
  useCCCreatePlanningMutation,
  useCCHrSystemConflictResolveMutation,
  useCCHrSystemConflictsQuery,
  useCCMonthScheduleQuery,
  useCCPlanningProcessQuery,
  useCCPublishPlanningMutation,
  useCCRegeneratePlanningMutation,
  useCCScheduleResetShiftTypeOptimisticMutation,
  useCCScheduleSetShiftTypeOptimisticMutation,
  useCCScheduleShiftsQuery,
  useCCScheduleSyncWorkdayMutation,
  useCCValidationQuery,
  useCCSetFixationTypeOptimisticMutation,
  useMyCCScheduleResetShiftTypeOptimisticMutation,
  useMyCCScheduleSetShiftTypeOptimisticMutation,
} from '@/queries/resource/schedule';
import { retrieveUnifiedError } from '@/api/utils';

export const useCCSchedulerData = createSchedulerDataHook({
  useMonthScheduleQuery: useCCMonthScheduleQuery,
  usePlanningProcessQuery: useCCPlanningProcessQuery,
  useScheduleShiftsQuery: useCCScheduleShiftsQuery,
});

export const useCCSchedulerMutationActions = createSchedulerMutationActionsHook({
  useCreatePlanningMutation: useCCCreatePlanningMutation,
  useRegeneratePlanningMutation: useCCRegeneratePlanningMutation,
  useCancelPlanningMutation: useCCCancelPlanningMutation,
  usePublishPlanningMutation: useCCPublishPlanningMutation,
  useCancelGenerationPlanningMutation: useCCCancelGenerationPlanningMutation,
});

export const useCCWorkdaySync = createWorkdaySyncHook({
  useWorkdaySyncMutation: useCCScheduleSyncWorkdayMutation,
  useGetWorkdayConflicts: useCCHrSystemConflictsQuery,
  useHrSystemConflictResolveMutation: useCCHrSystemConflictResolveMutation,
});

export const useCCSchedulerServices = (selectedDay: Date, shiftsData?: CCResourcesWithShifts) => {
  const userList = useMemo(() => {
    if (!shiftsData) return null;
    return Object.values(shiftsData).map((r) => r.resource);
  }, [shiftsData]);

  const resourceShiftTotalsMap = useMemo(() => {
    if (!shiftsData) return undefined;
    return getResourceShiftTotalsMap(selectedDay, shiftsData, 'shiftType');
  }, [selectedDay, shiftsData]);

  return {
    userList,
    resourceShiftTotalsMap,
  };
};

export const useCCShiftChange = createUseShiftChangeHook({
  useScheduleSetShiftTypeOptimisticMutation: useCCScheduleSetShiftTypeOptimisticMutation,
  useScheduleResetShiftTypeOptimisticMutation: useCCScheduleResetShiftTypeOptimisticMutation,
});

export const useCCUnblockShift = createUseUnblockShiftHook({
  useSetFixationTypeOptimisticMutation: useCCSetFixationTypeOptimisticMutation,
});

export const useMyCCShiftChange = (dateRange: DateRangeParams, monthSchedule?: MonthSchedule) => {
  const apiDateRange = useMemo(() => {
    return {
      from: withoutTzOffset(dateRange.from),
      to: withoutTzOffset(dateRange.to),
    };
  }, [dateRange]);

  const { mutateAsync: setMyShiftType } = useMyCCScheduleSetShiftTypeOptimisticMutation(apiDateRange);
  const { mutateAsync: resetMyShiftType } = useMyCCScheduleResetShiftTypeOptimisticMutation(apiDateRange);

  return useCallback(
    async (resourceId: string, day: string, payload?: SetCCShiftData) => {
      try {
        const scheduleId = monthSchedule?.scheduleId ?? monthSchedule?.preScheduleId;
        const isPreSchedule = !monthSchedule?.scheduleId && !!monthSchedule?.preScheduleId;

        if (scheduleId) {
          if (payload) {
            const required = false;
            const shiftStatus = ShiftStatus.Actual;

            await setMyShiftType({
              ...payload,
              scheduleId,
              resourceId,
              day,
              shiftStatus,
              isEditable: true,
              isPreSchedule,
              required,
            });
          } else {
            await resetMyShiftType({ scheduleId, resourceId, day });
          }
        }
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [monthSchedule, setMyShiftType, resetMyShiftType],
  );
};

export const useCCScheduleValidation = (
  planningData?: PlanningProcess,
  shiftsData?: CCResourcesWithShifts,
  enabled?: boolean,
) => {
  const { scheduleId, status } = planningData ?? {};
  const isEditing = status === PlanningProcessStatus.Editing;
  const canValidate = Boolean(enabled && scheduleId && isEditing);
  const { data, isFetching } = useCCValidationQuery(scheduleId, canValidate);
  const fillingState = data?.scheduleSize.fillingState;

  const { byDayMap, dayMessagesList } = useMemo(() => {
    return transformCountryViolations(data);
  }, [data]);

  const { byResourceMap, resourcesMessagesList } = useMemo(() => {
    return transformResourcesViolations(data?.resourceRulesViolations, shiftsData);
  }, [data?.resourceRulesViolations, shiftsData]);

  const count = useMemo(() => {
    const allMessages = [...dayMessagesList, ...resourcesMessagesList].flatMap((v) => v.messages);
    return allMessages.length;
  }, [dayMessagesList, resourcesMessagesList]);

  if (!enabled) return undefined;

  return {
    count,
    byDayMap,
    byResourceMap,
    dayMessagesList,
    resourcesMessagesList,
    isFetching,
    fillingState,
    publishReadiness: data?.publishReadiness,
  };
};

export const useCCSchedulerShiftHighlight = createSchedulerShiftHighlightHook({
  useSetHighlightValue: useCCSchedulerHighlightStore.use.setHighlightValue,
});
